import * as React from 'react';
import * as autoBind from 'react-autobind';
import {MyWindow} from '../../types/MyWindow';
import {AnnotationModes, MobileViewingModes, QuizState, UserTypes} from '../../globals/Constants';
import IVideoPlayer from '../../types/IVideoPlayer';
import SmartplayerMediaWrapper from './SmartplayerMediaWrapper';
import MediaDimensionsModel from '../../types/MediaDimensionsModel';
import ISmartPlayerVideoPlayer from '../../types/ISmartPlayerVideoPlayer';
import SmartPlayerCommentMarkersPlugin from './SmartPlayerCommentMarkersPlugin';
import SmartPlayerAnnotationPlugin from './SmartPlayerAnnotationPlugin';
import Themeable from '../../types/Themeable';
import ReactResizeDetector from 'react-resize-detector';
import * as classNames from 'classnames';
import UserModel from '../../types/UserModel';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';

declare const window: MyWindow;

export default class SmartplayerVideo extends React.Component<SmartplayerVideoProps, SmartPlayerVideoState> {
   readonly state: SmartPlayerVideoState = {showPoster: false};
   player: IVideoPlayer;
   spContainer: HTMLElement;
   videoElement: HTMLElement;
   smartPlayerInitializedPromise: Promise<ISmartPlayerVideoPlayer>;
   hasPlayed: boolean;
   canPlay: boolean;

   constructor(props: SmartplayerVideoProps) {
      super(props);
      autoBind(this);

      this.smartPlayerInitializedPromise = new Promise(resolve => {
         const waitTime = 100;

         const checkSmartPlayerInitialized = () => {
            if (window.TSC && window.TSC.mediaInterface) {
               resolve(window.TSC.mediaInterface);
            } else {
               setTimeout(checkSmartPlayerInitialized, waitTime);
            }
         };

         checkSmartPlayerInitialized();
      });
   }

   componentDidMount() {
      const {src, themeName, setPlayer, xmpSrc, audioDescriptionMediaSrc, quizId, accessTokenEndpoint, currentUser, environment} = this.props;

      const isSignedIn = currentUser && currentUser.userId && currentUser.userType !== UserTypes.selfIdentified;

      this.smartPlayerInitializedPromise.then(mediaInterface => {
         if (mediaInterface.setTheme) {
            mediaInterface.setTheme(themeName);
         }
         mediaInterface.authInfo = isSignedIn ? {accessTokenEndpoint: accessTokenEndpoint} : {};
         mediaInterface.environment = environment;
         mediaInterface.quizId = quizId;
         mediaInterface.xmpSrc = xmpSrc;
         mediaInterface.audioDescriptionMediaSrc = audioDescriptionMediaSrc;
         mediaInterface.ignoreXmpControllerParameters = ['autohide', 'autostart', 'autoplay', 'backgroundcolor', 'endaction', 'endactionparam', 'unicodeenabled'];
         mediaInterface.ignoreQuizQuestions = false;
         mediaInterface.addSrc(src);
         mediaInterface.preload = 'metadata';
         mediaInterface.controls = 'true';
         mediaInterface.doubleClickControlEnabled = true;
         mediaInterface.disableInteractions = false;
         mediaInterface.useLargerProgressBar = true;
         mediaInterface.singleClickControlEnabled = false;
         mediaInterface.endActionType = 'stop';
         mediaInterface.endActionParam = 'false';
         mediaInterface.run(this.spContainer.id);
         mediaInterface.disablePictureInPicture = 'false';

         this.player = new SmartplayerMediaWrapper(mediaInterface);
         setPlayer(this.player);

         window.addEventListener('resize', this.getVideoDimensions);
         mediaInterface.addEventListener(window.TSC.events.External.PlayerFullScreenModeChanged, this.onSmartPlayerFullScreenChange);
         mediaInterface.addEventListener(window.TSC.events.External.QuizVisible, this.onQuizQuestionShow);
         mediaInterface.addEventListener(window.TSC.events.External.QuizHidden, this.onQuizHide);
         mediaInterface.addEventListener(window.TSC.events.External.ShowStartQuizPrompt, this.onQuizSetupShow);
         mediaInterface.addEventListener(window.TSC.events.External.HideStartQuizPrompt, this.onQuizHide);
         mediaInterface.addEventListener(window.TSC.events.External.PlayerRumData, this.onRumData);
         mediaInterface.addEventListener('playing', () => {
            this.hasPlayed = true;
         });

         // Hax for IE11. It behaves weirdly at the end of video sometimes.
         mediaInterface.addEventListener('play', () => {
            if (this.player.getCurrentTime() >= this.player.getDuration()) {
               this.player.setCurrentTime(0);
               this.player.play();
            }
         });

         // Hax for IE11. It behaves weirdly at the end of video sometimes.
         mediaInterface.addEventListener('timeupdate', () => {
            if (this.player.getCurrentTime() >= this.player.getDuration()) {
               this.player.pause();
            }
         });

         mediaInterface.addEventListener('loadedmetadata', () => {
            this.videoElement = this.spContainer.getElementsByTagName('video')[0];
            this.getVideoDimensions();
         });

         // IOS does not trigger canplay until we click play, so it needs the poster displayed
         mediaInterface.addEventListener('canplay', () => {
            this.canPlay = true;
            if (this.state.showPoster) {
               this.setState({showPoster: false});
            }
         });

         setTimeout(() => {
            if (!this.canPlay) {
               this.setState({showPoster: true});
            }
         }, 500);
      });
   }

   componentWillUnmount() {
      window.removeEventListener('resize', this.getVideoDimensions);
      this.smartPlayerInitializedPromise.then(mediaInterface => {
         mediaInterface.removeEventListener(window.TSC.events.External.PlayerFullScreenModeChanged, this.onSmartPlayerFullScreenChange);
         mediaInterface.removeEventListener(window.TSC.events.External.QuizVisible, this.onQuizQuestionShow);
         mediaInterface.removeEventListener(window.TSC.events.External.QuizHidden, this.onQuizHide);
         mediaInterface.removeEventListener(window.TSC.events.External.ShowStartQuizPrompt, this.onQuizSetupShow);
         mediaInterface.removeEventListener(window.TSC.events.External.HideStartQuizPrompt, this.onQuizHide);
         mediaInterface.removeEventListener(window.TSC.events.External.PlayerRumData, this.onRumData);
         mediaInterface.destroy();
      });
      this.player && this.player.dispose();
   }

   componentDidUpdate(prevProps: SmartplayerVideoProps) {
      const {themeName, annotationMode, mediaDimensions} = this.props;

      this.smartPlayerInitializedPromise.then(mediaInterface => {
         if (prevProps.annotationMode !== annotationMode) {
            if (annotationMode === AnnotationModes.edit) {
               mediaInterface.controls = 'false';
            } else {
               mediaInterface.controls = mediaInterface.isInFullScreenMode ? 'autohide' : 'true';
            }
         }

         if (prevProps.themeName !== themeName && mediaInterface.setTheme) {
            mediaInterface.setTheme(themeName);
         }

         if (annotationMode !== AnnotationModes.edit && prevProps.annotationMode === AnnotationModes.edit) {
            mediaInterface.doubleClickControlEnabled = true;
            mediaInterface.disableInteractions = false;
         } else if (annotationMode === AnnotationModes.edit && prevProps.annotationMode !== AnnotationModes.edit) {
            mediaInterface.doubleClickControlEnabled = false;
            mediaInterface.disableInteractions = true;
         }

         if (!prevProps.mediaDimensions || prevProps.mediaDimensions.width !== mediaDimensions.width) {
            this.onResize();
         }
      });
   }

   onSmartPlayerFullScreenChange() {
      this.smartPlayerInitializedPromise.then(mediaInterface => {
         mediaInterface.controls = mediaInterface.isInFullScreenMode ? 'autohide' : 'true';
         this.getVideoDimensions();
      });
   }

   getSmartPlayerControlsElement() {
      // patch internal breaking change for Smart Player
      return document.getElementById('controls') || document.getElementsByClassName('video-controls-container')[0] as HTMLElement;
   }

   onQuizSetupShow() {
      this.props.setQuizState(QuizState.setupOpen);
      this.getSmartPlayerControlsElement().style.visibility = 'hidden';
   }

   onQuizQuestionShow() {
      this.props.setQuizState(QuizState.questionOpen);
      this.getSmartPlayerControlsElement().style.visibility = 'hidden';
   }

   onQuizHide() {
      this.props.setQuizState(QuizState.closed);
      this.getSmartPlayerControlsElement().style.visibility = 'visible';
   }

   onRumData(data: any) {
      const product = 'VideoReview';

      if (data && data.rum) {
         data.rum.forEach((rumItem: ISmartPlayerRumDataEvent) => {
            GoogleAnalyticsService.trackRumData(product, rumItem.name, rumItem.time);
            window.appInsights.trackEvent({name: 'SmartPlayerRUM', properties: {Product: product, Name: rumItem.name, Time: rumItem.time}});
         });
      }
   }

   getVideoDimensions() {
      this.smartPlayerInitializedPromise.then(mediaInterface => {
         const {setMediaDimensions, mediaDimensions} = this.props;

         if (!setMediaDimensions || !this.videoElement) {
            return;
         }

         if (mediaInterface.isInFullScreenMode) {
            const aspectRatio = mediaDimensions.height / mediaDimensions.width;
            let newHeight = this.videoElement.offsetHeight;
            const newWidth = this.videoElement.offsetWidth;
            const newAspectRatio = newHeight / newWidth;
            if (newAspectRatio > aspectRatio) {
               newHeight = newWidth * aspectRatio;
            }

            setMediaDimensions({
               width: newWidth,
               height: newHeight
            });

            return;
         }

         setMediaDimensions({
            width: this.videoElement.offsetWidth,
            height: this.videoElement.offsetHeight
         });
      });
   }

   setSpContainerRef(spContainer: HTMLElement) {
      this.spContainer = spContainer;
   }

   onAccessibilitPlayKeyPress(e: any): void {
      e.stopPropagation();
      this.player.getIsPaused() && this.props.annotationMode !== AnnotationModes.edit ? this.player.play() : this.player.pause();
   }

   onResize() {
      this.getVideoDimensions();
      const {mediaDimensions, srcMediaDimensions} = this.props;
      const controlsElement = this.getSmartPlayerControlsElement();

      if (!controlsElement) {
         return;
      }

      if (!mediaDimensions) {
         controlsElement.style.display = 'none';
         return;
      }

      const mediaAspectRatio = srcMediaDimensions.width / srcMediaDimensions.height;
      const containerAspectRatio = mediaDimensions.width / mediaDimensions.height;
      const isFullScreen = this.spContainer.classList.contains('fullscreen-mode');

      controlsElement.style.display = 'block';
      if (isFullScreen) {
         controlsElement.style.width = this.spContainer.offsetWidth.toString() + 'px';
      } else {
         if (containerAspectRatio < mediaAspectRatio) {
            controlsElement.style.width = mediaDimensions.width.toString() + 'px';
         } else {
            const calcWidth = mediaDimensions.height * (mediaAspectRatio);
            controlsElement.style.width = calcWidth.toString() + 'px';
         }
      }
   }

   render() {
      const {mediaDimensions, storeAnnotation, posterSrc, annotationMode, mobileViewingMode} = this.props;
      const containerClasses = classNames('video-container', {annotating: annotationMode === AnnotationModes.edit}, {'feedback-mode': mobileViewingMode === MobileViewingModes.feedback});

      return (
         <div className={containerClasses}>
            <ReactResizeDetector handleWidth handleHeight skipOnMount onResize={this.onResize} />
            <div id="sp-container" ref={this.setSpContainerRef} style={posterSrc && this.state.showPoster ? {backgroundImage: `url("${posterSrc}")`, backgroundSize: 'cover'} : {}} />
            <SmartPlayerCommentMarkersPlugin smartPlayerInitializedPromise={this.smartPlayerInitializedPromise} />
            {mediaDimensions && <SmartPlayerAnnotationPlugin smartPlayerInitializedPromise={this.smartPlayerInitializedPromise} storeAnnotation={storeAnnotation} />}
         </div>
      );
   }
}

interface SmartPlayerVideoState {
   showPoster: boolean;
}

interface ISmartPlayerRumDataEvent {
   name: string;
   time: number;
}

export interface SmartplayerVideoProps extends Themeable {
   src: string;
   annotationMode: AnnotationModes;
   mediaDimensions: MediaDimensionsModel;
   posterSrc: string;
   xmpSrc: string;
   audioDescriptionMediaSrc: string;
   quizId: string;
   accessTokenEndpoint: string;
   srcMediaDimensions: MediaDimensionsModel;
   mobileViewingMode: MobileViewingModes;
   currentUser: UserModel;
   environment: string;
   storeAnnotation(annotation: string): void;
   setPlayer(player: IVideoPlayer): void;
   setMediaDimensions(dimensions: MediaDimensionsModel): void;
   setQuizState(quizState: QuizState): void;
}
